import { ArrowUpCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAppUpdate } from '@/hooks/useAppUpdate';

// A dismissible "a new version is available" banner. Shown only when the App
// Store has a newer native build than the one running (useAppUpdate is
// native-only, so this renders nothing on web). Pinned to the bottom so it sits
// clear of the top status bar / notch; tapping opens the App Store.
//
// Worded as an explicit "App Store update" / "Open App Store" so it is NOT
// confused with the frequent OTA refresh banner (OtaUpdateBanner), which just
// reloads the in-app JS. This one leaves the app to download a new native build.
export function UpdateAvailableBanner() {
  const { updateAvailable, storeVersion, dismiss, openStore } = useAppUpdate();

  if (!updateAvailable) return null;

  return (
    <div
      role="status"
      className="fixed inset-x-0 bottom-0 z-[60] border-t border-border bg-card px-4 py-3 shadow-lg"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)' }}
    >
      <div className="mx-auto flex max-w-xl items-center gap-3">
        <ArrowUpCircle className="h-5 w-5 shrink-0 text-primary" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium leading-tight">App Store update</p>
          <p className="truncate text-xs text-muted-foreground">
            {storeVersion
              ? `Version ${storeVersion} is ready in the App Store.`
              : 'A new version is ready in the App Store.'}
          </p>
        </div>
        <Button size="sm" onClick={openStore} className="shrink-0">
          Open App Store
        </Button>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss update notice"
          className="shrink-0 rounded-md p-1 text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
